import { memo, useId, useState, type CSSProperties } from 'react';
import clsx from 'clsx';
import { PiCaretUpFill, PiLinkSimpleLight } from 'react-icons/pi';
import ConditionalRender from '../../../../../shared/components/ConditionalRender';
import TruncatedText from '../../../../../shared/components/TruncatedText';
import type { AssetListItem } from '../../../../../types/InfrastructureManagerTypes';
import AssetWorkOrderDetails from './AssetWorkOrderDetails';
import styles from './AssetListPanel.module.scss';

interface AssetListRowProps {
  asset: AssetListItem;
  assetKey: string;
  index: number;
  top: number;
  isExpanded: boolean;
  isSelected: boolean;
  measureRef: (node: HTMLElement | null) => void;
  onToggleExpanded: (assetKey: string) => void;
  onToggleSelect: (asset: AssetListItem) => void;
}

// Memoised so scrolling, expanding or selecting one row leaves the others untouched.
const AssetListRow = ({ asset, assetKey, index, top, isExpanded, isSelected, measureRef, onToggleExpanded, onToggleSelect }: AssetListRowProps) => {
  const [hasIconError, setHasIconError] = useState(false);
  const detailsId = useId();

  const handleToggleExpanded = () => onToggleExpanded(assetKey);
  const handleToggleSelect = () => onToggleSelect(asset);
  const handleIconError = () => setHasIconError(true);

  // Runtime offset reaches the module through a custom property, as in ColorSwatch.
  const positionStyle = { '--asset-row-top': `${top}px` } as CSSProperties;

  return (
    <div
      ref={measureRef}
      data-index={index}
      role="listitem"
      className={clsx(styles.assetListItem, isExpanded && styles.assetListItemExpanded, isSelected && styles.assetListItemSelected)}
      style={positionStyle}
    >
      <div className={styles.assetListRow}>
        <button
          type="button"
          className={styles.assetListRowExpand}
          onClick={handleToggleExpanded}
          aria-expanded={isExpanded}
          aria-controls={isExpanded ? detailsId : undefined}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${asset.displayValue}`}
        >
          <PiCaretUpFill className={clsx(styles.assetListRowCaret, isExpanded && styles.assetListRowCaretExpanded)} aria-hidden="true" />
        </button>

        <button type="button" className={styles.assetListRowSelect} onClick={handleToggleSelect} aria-pressed={isSelected}>
          <span className={styles.assetListRowName}>
            <TruncatedText text={asset.displayValue} />
          </span>
          {asset.iconUrl && !hasIconError ? (
            <img className={styles.assetListRowIcon} src={asset.iconUrl} alt="" loading="lazy" decoding="async" onError={handleIconError} />
          ) : (
            <PiLinkSimpleLight className={styles.assetListRowIcon} aria-hidden="true" />
          )}
        </button>
      </div>

      <ConditionalRender visible={isExpanded} hideFallback>
        <AssetWorkOrderDetails id={detailsId} layerId={asset.layerId} keyValue={asset.keyValue} />
      </ConditionalRender>
    </div>
  );
};

export default memo(AssetListRow);
